import React from 'react';
import _isEmpty from 'lodash/isEmpty';
import Button, {
  ButtonProps,
  ButtonSizes,
  ButtonTypes,
} from '@lib/components/Button/Button';
import MaterialIcon from '@lib/components/MaterialIcon/MaterialIcon';
import Tooltip from '@lib/components/Tooltip/Tooltip';
import { TooltipShape } from '@lib/components/Input/enums';
import styles from '../SelectPanel.module.scss';

type SelectPanelButtonProps = {
  buttonProps?: ButtonProps;
  computedButtonCounter?: number;
  disabled?: boolean;
  disabledTooltipProps?: TooltipShape;
  isPanelOpen: boolean;
  label?: string;
  onReset: (event: React.MouseEvent<HTMLElement>) => void;
  onTrigger: (handleToggle: (next?: boolean) => void) => void;
};

export function SelectPanelButton({
  buttonProps,
  computedButtonCounter,
  disabled,
  disabledTooltipProps,
  isPanelOpen,
  label,
  onReset,
  onTrigger,
}: SelectPanelButtonProps) {
  const isDisabled = disabled ?? buttonProps?.disabled;
  const isClearButtonVisible = !isDisabled && !!computedButtonCounter;

  let rightIcon = isPanelOpen ? 'arrow_drop_up' : 'arrow_drop_down';
  if (isDisabled) {
    rightIcon = 'lock';
  }

  const mergedButtonProps: ButtonProps = {
    ...buttonProps,
    buttonText: label,
    buttonSize: ButtonSizes.large,
    buttonType: ButtonTypes.secondaryOutlined,
    counter: computedButtonCounter,
    isFocused: isPanelOpen,
    disabled: isDisabled,
    rightIcon,
    rightIconClassName: isClearButtonVisible
      ? styles.hiddenRightIcon
      : undefined,
  };

  const handleTriggerClick = (
    handleToggle: (next?: boolean) => void,
    event: React.MouseEvent<HTMLElement>,
  ) => {
    if (mergedButtonProps.disabled) return;
    buttonProps?.onClick?.(event);
    onTrigger(handleToggle);
  };

  const isDisabledTooltipVisible =
    isDisabled && !_isEmpty(disabledTooltipProps);

  const renderTrigger = ({
    handleToggle,
  }: {
    handleToggle: (next?: boolean) => void;
  }) => {
    const button = (
      <Button
        {...mergedButtonProps}
        onClick={(event) => handleTriggerClick(handleToggle, event)}
      />
    );

    return (
      <div className={styles.buttonWrapper}>
        {isDisabledTooltipVisible ? (
          <Tooltip maxWidth={160} {...disabledTooltipProps}>
            {button}
          </Tooltip>
        ) : (
          button
        )}
        {isClearButtonVisible && (
          <MaterialIcon
            icon="cancel"
            className={styles.cancelButton}
            onClick={onReset}
          />
        )}
      </div>
    );
  };

  return renderTrigger;
}
